import React, { useState } from 'react';
import ReactDatePicker, {
  registerLocale,
  setDefaultLocale
} from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styled from 'styled-components/macro';

import { Field } from 'formik';
import { FormControl } from '@material-ui/core';
import OutlinedInput from '../../components/OutlinedInput';
import { connect } from 'react-redux';
import dotProp from 'dot-prop';
import useTranslations from '../../hooks/useTranslations';
import FormHelperText from '../../internal-components/form-builder/FormHelperText';

import enGB from 'date-fns/locale/en-GB';
registerLocale('en-GB', enGB);
setDefaultLocale('en-GB');

const FieldWrapper = styled.div`
  text-align: left;

  .react-datepicker {
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: ${({ theme }) => theme.typography.fontSize}px;
  }

  .react-datepicker__input-container {
    width: 100%;
  }
`;

export const DatePicker = React.forwardRef(
  (
    {
      onCalendarClose,
      onCalendarOpen,
      onChange,
      onBlur,
      value,
      required,
      disabled,
      helperText,
      label,
      minwidth,
      customInput,
      dateInputProps,
      placeholderText,
      minDate,
      maxDate,
      ...props
    },
    ref
  ) => (
    <ReactDatePicker
      onCalendarClose={onCalendarClose}
      onCalendarOpen={onCalendarOpen}
      onChange={onChange}
      onBlur={onBlur}
      selected={value ? new Date(value) : null}
      showMonthDropdown
      showYearDropdown
      placeholderText={placeholderText || 'DD/MM/YYYY'}
      minDate={minDate}
      maxDate={maxDate}
      customInput={
        customInput || (
          <OutlinedInput
            fullWidth
            label={label}
            inputProps={{ 'data-testid': props['data-testid'] }}
            minwidth={minwidth}
            {...dateInputProps}
          />
        )
      }
      required={required}
      disabled={disabled}
      data-testid={props['data-testid']}
      aria-describedby={helperText}
      dropdownMode="select"
      ref={ref}
      locale="en-GB"
      dateFormat="dd/MM/yyyy"
      {...props}
    />
  )
);

export function DateField({
  label,
  required,
  disabled,
  conditions,
  state,
  dispatch,
  defaultValue,
  maxLength,
  title,
  description,
  fullWidth,
  InputLabelProps,
  ...props
}) {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Field
      {...props}
      validate={value =>
        !value && required ? 'forms.field_required' : undefined
      }
      children={({ field, form: { touched, errors, values, ...form } }) => {
        const error = touched[field.name] && errors[field.name];

        if (defaultValue && !form.dirty) {
          field.value = dotProp.get(state, defaultValue);
        }

        const { value } = field;

        const onChange = v => {
          var newFieldValue;
          if (v === '' || v === null) {
            form.setFieldValue(field.name, '');
          }

          if (v instanceof Date) {
            newFieldValue = +v - v.getTimezoneOffset() * 60000;
            form.setFieldValue(field.name, newFieldValue);
          }
        };

        const onCalendarOpen = () => {
          setIsOpen(true);
        };

        const onCalendarClose = () => {
          setIsOpen(false);
        };

        const onBlur = () => form.setFieldTouched(field.name, true);

        var displayValue = value === '' || value === null ? '' : new Date(value);
        if (displayValue) {
          displayValue =
            +displayValue + displayValue.getTimezoneOffset() * 60000;
        }

        return (
          <FieldWrapper
            style={{ zIndex: isOpen ? 9999 : null, position: 'relative' }}
          >
            <FormControl
              style={{ width: '100%' }}
              error={!!error}
              fullWidth={fullWidth}
            >
              <DatePicker
                onCalendarClose={onCalendarClose}
                onCalendarOpen={onCalendarOpen}
                onChange={onChange}
                onBlur={onBlur}
                value={displayValue}
                required={required}
                disabled={disabled}
                popperContainer={p => (
                  <div data-testid={`datepicker-popper-${field.name}`} {...p} />
                )}
                locale="en-GB"
                dateFormat="dd/MM/yyyy"
                {...props}
              />
              <FormHelperText>{t(error || '')}</FormHelperText>
            </FormControl>
          </FieldWrapper>
        );
      }}
    />
  );
}

export default connect(state => ({ state }))(DateField);
